import {catchError, map, Observable, of} from 'rxjs';

import {
	IAddAttachmentRequest,
	IAddAttachmentResponse,
	IField,
	IGetDocumentRequestParams,
	IGetDocumentResponse,
	IGetMergeDocumentsAndFieldsRequest,
	IGetMergeDocumentsAndFieldsResponse,
	IKeepOriginalDocumentRequest,
} from '@core/api/data-access';
import {StoredFile} from '@global-data';

import {CyclesService} from './cycles.service';

export class DocumentCyclesService {
	constructor(private cyclesService: CyclesService) {}

	/**
	 * Get cycle shared
	 * @param cycleId - cycle id
	 * @param flatten
	 * @param compress - true if response should be GZiped
	 */
	public getDocument(cycleId: string, flatten = false, compress = false): Observable<StoredFile> {
		const requestParams: IGetDocumentRequestParams = {id: cycleId, flatten, compress};
		return this.cyclesService.apiService.get<IGetDocumentResponse>(this.cyclesService.getUrl(`document`), requestParams).pipe(map(response => new StoredFile(response.document)));
	}

	/**
	 * Downloads the document by cycle id
	 * @param cycleId
	 * @param downloadFileName
	 */
	public downloadDocument(cycleId: string, downloadFileName: string): Observable<boolean> {
		return this.getDocument(cycleId, true, false).pipe(
			map(pdfDocument => {
				downloadFileName = cleanDownloadFileName(downloadFileName, 'pdf');
				pdfDocument.downloadFile(downloadFileName);
				return true;
			}),
			catchError(err => {
				console.error(err);
				return of(false);
			}),
		);
	}

	/**
	 * Merges the files and returns the list of fields
	 * @param files
	 */
	public mergeBase64PdfFiles(files: string[]): Observable<{mergedFile: StoredFile; fields: IField[]; originalResponse?: IGetMergeDocumentsAndFieldsResponse}> {
		const requestParams: IGetMergeDocumentsAndFieldsRequest = {files};
		if (files.length === 0) return of({fields: [], mergedFile: null});
		if (files.length === 1) {
			return this.cyclesService.Fields.getDocumentFields(files[0]).pipe(
				map(resFields => {
					return {fields: resFields?.fields || [], mergedFile: new StoredFile(files[0])};
				}),
			);
		} else {
			return this.cyclesService.apiService.post<IGetMergeDocumentsAndFieldsResponse, IGetMergeDocumentsAndFieldsRequest>(this.cyclesService.getUrl(`mergedfields`), requestParams).pipe(
				map(response => {
					const mergedFile = new StoredFile(response.fileData);
					const fields = response.fields;
					return {mergedFile, fields, originalResponse: response};
				}),
			);
		}
	}

	/**
	 * Adds Attachment to cycle
	 * @param request
	 */
	public addAttachment(request: IAddAttachmentRequest): Observable<IAddAttachmentResponse> {
		return this.cyclesService.apiService.put<IAddAttachmentResponse, IAddAttachmentRequest>(this.cyclesService.getUrl(`document`), request);
	}

	/**
	 * Override the kept original of the cycle
	 * @param cycleId
	 * @param fileBase64
	 */
	public keepOriginalDocument(cycleId: string, fileBase64: string): Observable<void> {
		return this.cyclesService.apiService.post<void, IKeepOriginalDocumentRequest>(this.cyclesService.getUrl(`original`), {cycleId, original: fileBase64});
	}
}

function cleanDownloadFileName(downloadFileName: string, wantedExtension: string): string {
	// remove extension, replae '.' with ' '
	downloadFileName = downloadFileName.replace(/\.[^/.]+$/, '').replace(/\./g, ' ');
	// add wanted extension
	return downloadFileName + '.' + wantedExtension;
}
